// AICA Records Screen - Record Management
// Master-Detail + Table view toggle
// Phase 2.9: Added bulk selection, keyboard shortcuts, batch upload
// Phase 3: Refactored to use extracted hooks (God Component decomposition)
// Phase 4: Master-Detail layout + Table view toggle
// Phase 5: Split into focused modules under routes/records/
import { createFileRoute } from '@tanstack/react-router';
import { RECORD_OUTCOME_FILTERS, type RecordOutcomeFilter } from '@aica/api-types';
import type { ViewMode, SearchParams } from './records/types';
import { RecordsPage } from './records/RecordsPage';
import { scriptStepFilterParam } from '@/lib/records/script-step-filter';
import { parseCriteriaSearchParam } from '@/lib/records/criteria-filter';
import { CALL_CATEGORY_OPTIONS } from '@/lib/records/categories';

/** An ISO timestamp from the URL, or nothing — a hand-edited link must not break the list. */
function isoInstant(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 40) return undefined;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return undefined;
  const iso = new Date(ms).toISOString();
  // A year outside 0000–9999 comes out as «+010000-…», which the list API's
  // datetime check rejects — the whole list would answer 400.
  return /^\d{4}-/.test(iso) ? iso : undefined;
}

/** A flag in the URL: `?scored=1` arrives as a number, a hand-typed link as a string. */
function urlFlag(value: unknown): true | undefined {
  return value === true || value === 1 || value === '1' || value === 'true'
    ? true
    : undefined;
}

/** A string parameter with something in it; `?search=` names no filter. */
function nonEmpty(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * The AI-accuracy rating and how to compare it (WS7-T9), as one filter: a
 * typed navigate passes a number, a hand-typed/serialized URL a string —
 * an integer 1–5 is accepted, anything else rejected; a mode without a rating
 * filters nothing and is dropped with it.
 */
function agentRatingFrom(
  search: Record<string, unknown>
): Pick<SearchParams, 'agentRating' | 'agentRatingMode'> {
  const n = Number(search.agentRating);
  if (!Number.isInteger(n) || n < 1 || n > 5) {
    return { agentRating: undefined, agentRatingMode: undefined };
  }
  const mode = search.agentRatingMode;
  return {
    agentRating: n,
    agentRatingMode: mode === 'gte' || mode === 'eq' || mode === 'lte' ? mode : undefined,
  };
}

/**
 * The report's window, whole or not at all: a backwards one would only empty
 * the list under its banner, and half of one has no banner to name it — the
 * page would drop it silently.
 */
function reportWindowFrom(search: Record<string, unknown>) {
  const createdAfter = isoInstant(search.createdAfter);
  const createdBefore = isoInstant(search.createdBefore);
  if (!createdAfter || !createdBefore || createdAfter > createdBefore) {
    return { createdAfter: undefined, createdBefore: undefined };
  }
  return { createdAfter, createdBefore };
}

// No loader: it prefetched `recordsListOptions` — a query key the page never
// reads (the list is an infinite query with its own filters) — so every visit
// waited on a request whose answer was thrown away (code-review of #1282).
export const Route = createFileRoute('/records')({
  component: RecordsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      id: typeof search.id === 'string' ? search.id : undefined,
      recordId: typeof search.recordId === 'string' ? search.recordId : undefined,
      // A filter parameter the page cannot apply (empty, unknown, half of a
      // pair) is dropped here: present, it would count as a link's filter set
      // and replace the reader's remembered one with nothing (#1289).
      agentId: nonEmpty(search.agentId),
      projectId: nonEmpty(search.projectId),
      projectIds: nonEmpty(search.projectIds),
      search: nonEmpty(search.search),
      callCategory: (CALL_CATEGORY_OPTIONS as readonly unknown[]).includes(
        search.callCategory
      )
        ? (search.callCategory as string)
        : undefined,
      callDirection:
        search.callDirection === 'inbound' ||
        search.callDirection === 'outbound' ||
        search.callDirection === 'unknown'
          ? search.callDirection
          : undefined,
      ...agentRatingFrom(search),
      view:
        search.view === 'list' || search.view === 'table'
          ? (search.view as ViewMode)
          : undefined,
      t: (() => {
        if (search.t === '' || search.t === null || search.t === undefined) {
          return undefined;
        }
        const value = Number(search.t);
        return Number.isSafeInteger(value) && value >= 0 ? value : undefined;
      })(),
      clip: (() => {
        if (search.clip === '' || search.clip === null || search.clip === undefined) {
          return undefined;
        }
        const value = Number(search.clip);
        return Number.isSafeInteger(value) && value >= 1 && value <= 120
          ? value
          : undefined;
      })(),
      alertId:
        typeof search.alertId === 'string' && search.alertId.length <= 200
          ? search.alertId
          : undefined,
      outcome: (RECORD_OUTCOME_FILTERS as readonly string[]).includes(
        search.outcome as string
      )
        ? (search.outcome as RecordOutcomeFilter)
        : undefined,
      type: search.type === 'call' || search.type === 'chat' ? search.type : undefined,
      ...reportWindowFrom(search),
      scored: urlFlag(search.scored),
      mine: urlFlag(search.mine),
      // Условия по критериям (#865) — строками, и только разборчивые той же
      // схемой, что на сервере (`parseCriteriaSearchParam`): неразборчивая
      // ссылка не применяется вовсе — обычный список лучше, чем 400 и пустой
      // экран (#1293), — и набором фильтров не считается.
      criterion: (() => {
        const raw = search.criterion;
        const list = typeof raw === 'string' ? [raw] : Array.isArray(raw) ? raw : [];
        const strings = list.filter((item): item is string => typeof item === 'string');
        return parseCriteriaSearchParam(strings).length > 0 ? strings : undefined;
      })(),
      // Шаг скрипта, не выполненный в звонках (#879): строка не той формы, как
      // и повтор параметра, в адрес не попадает — иначе список навсегда ушёл бы
      // в 400 под чипом, который «применён».
      scriptStep: scriptStepFilterParam(search.scriptStep),
    };
  },
});
